'use client';

import { Contract } from '@/lib/types';
import Image from '@/components/ui/Image';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ScrollArea } from '../ui/scroll-area';
import ReactMarkdown from 'react-markdown';

interface ContractCardProps {
    contract: Contract;
    perspective: 'emitter' | 'recipient' | 'admin';
    isDetailView?: boolean;
    onSign?: (contractId: string) => void;
    onReject?: (contractId: string) => void;
}

export function ContractCard({ contract, perspective, isDetailView = false, onSign, onReject }: ContractCardProps) {
    
    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'Fecha no disponible';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
    };

    if (!isDetailView) {
        let title = '';
        let subtitle = '';

        switch (perspective) {
            case 'emitter':
                title = contract.recipientName;
                subtitle = `Para: ${contract.concept}`;
                break;
            case 'recipient':
                title = contract.emitterName;
                subtitle = `De: ${contract.concept}`;
                break;
            case 'admin':
                title = contract.concept;
                subtitle = `${contract.emitterName} -> ${contract.recipientName}`;
                break;
        }

        return (
            <Card className="hover:border-primary transition-colors">
                <CardHeader>
                    <CardTitle className="truncate">{title}</CardTitle>
                    <CardDescription className="truncate">{subtitle}</CardDescription>
                </CardHeader>
                <CardContent>
                    <p className="text-xs text-muted-foreground">{formatDate(contract.createdAt)}</p>
                    <p className={cn('text-xs font-bold', 
                        contract.status === 'signed' ? 'text-green-500' : 
                        contract.status === 'rejected' ? 'text-red-500' : 'text-yellow-500'
                    )}>
                        {contract.status.toUpperCase()}
                    </p>
                </CardContent>
            </Card>
        );
    }
    
    const Seal = ({ shieldUrl }: { shieldUrl: string }) => (
        <div className="relative h-16 w-32 mx-auto mt-1">
            <Image 
                src={shieldUrl} 
                alt="Sello de la entidad" 
                fill
                className="object-contain opacity-20 filter grayscale"
                style={{
                    filter: 'grayscale(100%) contrast(150%) brightness(60%) sepia(100%) hue-rotate(180deg) saturate(400%)',
                }}
            />
            <Image 
                src="https://www.transparenttextures.com/patterns/pinstripe.png" 
                alt="Textura de sello" 
                fill
                className="object-cover opacity-10 mix-blend-overlay"
            />
        </div>
    );

    return (
        <div className="bg-white text-black font-serif h-full w-full flex flex-col p-6 relative overflow-hidden">
            <div className="relative z-10 flex flex-col h-full">
                <header className="flex justify-between items-start mb-6">
                    <div className="flex flex-col items-center text-center w-20">
                        <Image src={contract.emitterShieldUrl} alt={contract.emitterName} width={48} height={48} className="object-contain h-12 w-12" />
                        <p className="text-[10px] mt-1 font-semibold leading-tight">{contract.emitterName}</p>
                    </div>
                    <div className="text-center pt-2 px-2">
                        <h2 className="text-lg font-bold underline">CONTRATO</h2>
                        <p className="text-xs mt-1">En Jartiland, a {formatDate(contract.createdAt)}</p>
                    </div>
                    <div className="flex flex-col items-center text-center w-20">
                        <Image src={contract.recipientShieldUrl} alt={contract.recipientName} width={48} height={48} className="object-contain h-12 w-12" />
                        <p className="text-[10px] mt-1 font-semibold leading-tight">{contract.recipientName}</p>
                    </div>
                </header>

                <main className="flex-grow flex flex-col min-h-0">
                    <h3 className="text-lg font-bold text-center mb-4">{contract.concept}</h3>
                    <ScrollArea className="flex-grow border-y border-gray-300 py-2 pr-2 h-72">
                        <div className="prose prose-sm max-w-none text-justify text-xs">
                            <ReactMarkdown>{contract.content}</ReactMarkdown>
                        </div>
                    </ScrollArea>
                </main>

                <footer className="mt-auto pt-6">
                    <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="relative">
                            <p className="font-semibold text-xs">FIRMADO: {contract.emitterName}</p>
                            <Seal shieldUrl={contract.emitterShieldUrl} />
                            <div className="absolute inset-x-0 bottom-0 flex justify-center items-center h-16">
                                 <Image src="https://i.imgur.com/V1uijQK.png" alt="Firma" width={128} height={64} className="object-contain w-24 h-12" />
                            </div>
                        </div>
                        <div className="relative">
                             <p className="font-semibold text-xs">FIRMADO: {contract.recipientName}</p>
                             {contract.status === 'signed' ? (
                                <>
                                    <Seal shieldUrl={contract.recipientShieldUrl} />
                                    <div className="absolute inset-x-0 bottom-0 flex justify-center items-center h-16">
                                        <Image src="https://i.imgur.com/V1uijQK.png" alt="Firma" width={128} height={64} className="object-contain w-24 h-12" />
                                    </div>
                                </>
                             ) : (
                                <div className="h-16 w-32 mx-auto mt-1 border-b-2 border-gray-400" />
                             )}
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
}
